import { Box, Checkbox, FormControlLabel } from "@mui/material";
import { PERMISSIONS, type Permission } from "@xop/dashboard-shared";
import type { JSX } from "react";

/**
 * The per-permission grant grid — a labelled checkbox per {@link PERMISSIONS}
 * entry. Shared by the per-user override editor (the expandable UserRow) and
 * the invite dialog's "add overrides" section. Controlled: `current` is the
 * granted set, `onChange` emits the next set on every toggle.
 */
export function PermissionEditor({
  current,
  onChange,
}: {
  current: Permission[];
  onChange: (next: Permission[]) => void;
}): JSX.Element {
  const set = new Set(current);
  return (
    <Box
      sx={{
        display: "grid",
        gap: 1,
        fontSize: 12,
        gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", md: "repeat(4, 1fr)" },
      }}
    >
      {PERMISSIONS.map((p) => (
        <FormControlLabel
          key={p}
          sx={{
            m: 0,
            gap: 1,
            borderRadius: 1,
            border: 1,
            borderColor: "divider",
            bgcolor: "background.default",
            px: 1,
            py: 0.75,
          }}
          control={
            <Checkbox
              size="small"
              sx={{ p: 0 }}
              checked={set.has(p)}
              onChange={(e) => {
                const next = new Set(set);
                if (e.target.checked) next.add(p);
                else next.delete(p);
                onChange([...next]);
              }}
            />
          }
          label={
            <Box component="span" sx={{ fontFamily: "ui-monospace, monospace", fontSize: 11 }}>
              {p}
            </Box>
          }
        />
      ))}
    </Box>
  );
}
